import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Users, UserCog, Plus, Edit, Trash2, Key, Power, Archive, Calendar, CheckSquare, Plane, MessageSquare, Clock, Check, X, Filter, Send, Paperclip } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { TOAST_MESSAGES } from '../utils/toastMessages';
import { validateFile, formatFileSize } from '../utils/fileHelpers';
import { uploadMultipleFiles } from '../utils/firebaseUpload';
import MessageAttachment from '../components/MessageAttachment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

const AdminOpsPage = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // UI State
  const [activeTab, setActiveTab] = useState('assistants'); // assistants | executives
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [assistantSubTab, setAssistantSubTab] = useState('profile'); // profile | tasks | calendar | travel | messages | time | messaging
  const [executiveSubTab, setExecutiveSubTab] = useState('profile'); // profile | preferences
  
  // Data State
  const [assistants, setAssistants] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [travelItems, setTravelItems] = useState([]);
  
  // Task Management
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    bucket: 'today',
    priority: 'medium',
    due_date: '',
    executive_id: ''
  });

  // Calendar Management
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [eventFormData, setEventFormData] = useState({
    title: '',
    start_datetime: '',
    end_datetime: '',
    location_type: 'Google Meet',
    location_link: '',
    location_address: '',
    notes: '',
    category: 'executive'
  });

  // Travel Management
  const [showAddTravelModal, setShowAddTravelModal] = useState(false);
  const [travelFormData, setTravelFormData] = useState({
    trip_name: '',
    type: 'flight',
    vendor: '',
    confirmation: '',
    travel_date: '',
    notes: ''
  });

  // Messages Management
  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  
  // Assistant Profile Editing
  const [assistantFormData, setAssistantFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    status: 'active',
    executive_ids: [],
    admin_notes: ''
  });
  
  // Modals
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showPasswordResultModal, setShowPasswordResultModal] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch assistants
      const assistRes = await fetch(`${BACKEND_URL}/api/admin/assistants`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (assistRes.ok) {
        const assistData = await assistRes.json();
        setAssistants(assistData.assistants || []);
      }

      // Fetch executives
      const execRes = await fetch(`${BACKEND_URL}/api/admin/assistants/executives/all`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (execRes.ok) {
        const execData = await execRes.json();
        setExecutives(execData.executives || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      showError('Failed to load data', 'Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssistantSelect = (assistant) => {
    console.log('🎯 Selecting assistant:', assistant.full_name, assistant.id);
    console.log('🎯 Current selectedAssistant:', selectedAssistant);
    
    // Clear executive selection
    setSelectedExecutive(null);
    
    // Set the selected assistant using functional update to ensure fresh state
    setSelectedAssistant(() => {
      const newSelection = {
        ...assistant,
        _timestamp: Date.now() // Force re-render
      };
      console.log('🎯 Setting new selectedAssistant:', newSelection);
      return newSelection;
    });
    
    // Reset to profile tab
    setAssistantSubTab('profile');
    
    // Set form data
    setAssistantFormData({
      full_name: assistant.full_name,
      email: assistant.email,
      phone: assistant.phone || '',
      status: assistant.is_active ? 'active' : 'disabled',
      executive_ids: assistant.executive_ids || [],
      admin_notes: assistant.admin_notes || ''
    });
    
    // Fetch assistant's data
    fetchAssistantTasks(assistant.id);
    fetchAssistantEvents(assistant.id);
    fetchAssistantTravel(assistant.id);
    
    console.log('✅ Selection complete');
  };

  const fetchAssistantTasks = async (assistantId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistantId}/tasks`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    }
  };

  const fetchAssistantEvents = async (assistantId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistantId}/calendar`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchAssistantTravel = async (assistantId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistantId}/travel`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setTravelItems(data.travel_items || []);
      }
    } catch (err) {
      console.error('Error fetching travel:', err);
    }
  };

  const fetchAssistantMessages = async (assistantId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/messages`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('assistantToken')}` // This would need admin implementation
        }
      });
      if (res.ok) {
        const data = await res.json();
        // Group messages by thread
        const threads = [];
        setMessageThreads(threads);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      // For now, set empty array
      setMessageThreads([]);
    }
  };

  const handleAddTask = async () => {
    if (!selectedAssistant || !taskFormData.title) return;
    
    setIsProcessing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({
          ...taskFormData,
          assistant_id: selectedAssistant.id
        })
      });

      if (res.ok) {
        success('Task added', 'Changes appear on assistant board immediately.');
        setShowAddTaskModal(false);
        setTaskFormData({
          title: '',
          description: '',
          bucket: 'today',
          priority: 'medium',
          due_date: '',
          executive_id: ''
        });
        fetchAssistantTasks(selectedAssistant.id);
      } else {
        showError('Couldn\'t add task', 'Try again.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!selectedAssistant) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'x-admin-key': ADMIN_KEY }
      });

      if (res.ok) {
        success('Task deleted', 'Update is live.');
        fetchAssistantTasks(selectedAssistant.id);
      } else {
        showError('Couldn\'t delete task', 'Try again.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
  };

  const handleAddEvent = async () => {
    if (!selectedAssistant || !eventFormData.title || !eventFormData.start_datetime) return;
    
    setIsProcessing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}/calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({
          ...eventFormData,
          assistant_id: selectedAssistant.id
        })
      });

      if (res.ok) {
        success('Event added', 'Update is live on assistant calendar.');
        setShowAddEventModal(false);
        setEventFormData({
          title: '',
          start_datetime: '',
          end_datetime: '',
          location_type: 'Google Meet',
          location_link: '',
          location_address: '',
          notes: '',
          category: 'executive'
        });
        fetchAssistantEvents(selectedAssistant.id);
      } else {
        showError('Couldn\'t add event', 'Try again.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!selectedAssistant) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}/calendar/${eventId}`, {
        method: 'DELETE',
        headers: { 'x-admin-key': ADMIN_KEY }
      });

      if (res.ok) {
        success('Event deleted', 'Update is live.');
        fetchAssistantEvents(selectedAssistant.id);
      } else {
        showError('Couldn\'t delete event', 'Try again.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
  };

  const handleAddTravel = async () => {
    if (!selectedAssistant || !travelFormData.trip_name) return;
    
    setIsProcessing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}/travel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({
          ...travelFormData,
          assistant_id: selectedAssistant.id
        })
      });

      if (res.ok) {
        success('Travel item added', 'Update is live in travel binder.');
        setShowAddTravelModal(false);
        setTravelFormData({
          trip_name: '',
          type: 'flight',
          vendor: '',
          confirmation: '',
          travel_date: '',
          notes: ''
        });
        fetchAssistantTravel(selectedAssistant.id);
      } else {
        showError('Couldn\'t add travel item', 'Try again.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteTravel = async (travelId) => {
    if (!selectedAssistant) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}/travel/${travelId}`, {
        method: 'DELETE',
        headers: { 'x-admin-key': ADMIN_KEY }
      });

      if (res.ok) {
        success('Travel item deleted', 'Update is live.');
        fetchAssistantTravel(selectedAssistant.id);
      } else {
        showError('Couldn\'t delete item', 'Try again.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
  };

  const handleExecutiveSelect = (executive) => {
    setSelectedExecutive(executive);
    setSelectedAssistant(null);
    setExecutiveSubTab('profile');
    
    // Initialize executive form data with all preferences
    setExecutiveFormData({
      full_name: executive.full_name || '',
      executive_id: executive.executive_id || '',
      title: executive.title || '',
      email: executive.email || '',
      phone: executive.phone || '',
      work_hours: executive.work_hours || 'Mon–Fri, 9am–5pm EST',
      timezone: executive.timezone || 'EST',
      preferred_contact_method: executive.preferred_contact_method || 'Email',
      
      // Travel preferences
      travel_airlines: executive.travel_preferences?.airlines || [],
      travel_seat_preference: executive.travel_preferences?.seat?.preference || 'aisle',
      travel_seat_cabin: executive.travel_preferences?.seat?.cabin || 'economy',
      travel_seat_location: executive.travel_preferences?.seat?.location || 'front',
      travel_hotels: executive.travel_preferences?.hotels?.preferred_brands || [],
      travel_rental_car_class: executive.travel_preferences?.rental_car?.class || 'midsize',
      travel_tsa_precheck: executive.travel_preferences?.tsa_programs?.tsa_precheck || false,
      travel_global_entry: executive.travel_preferences?.tsa_programs?.global_entry || false,
      
      // Food & Beverage
      dietary_restrictions: executive.food_beverage_preferences?.dietary_restrictions || [],
      allergies: executive.food_beverage_preferences?.allergies || [],
      beverages: executive.food_beverage_preferences?.beverages || [],
      favorite_cuisines: executive.food_beverage_preferences?.favorite_cuisines || [],
      food_notes: executive.food_beverage_preferences?.notes || '',
      
      // Meeting preferences
      meeting_platform: executive.meeting_preferences?.preferred_platform || 'Google Meet',
      meeting_buffer: executive.meeting_preferences?.buffer_minutes || 10,
      meeting_recording: executive.meeting_preferences?.recording_policy || 'allowed',
      meeting_agenda_required: executive.meeting_preferences?.agenda_required || false,
      
      // Assistant guidelines
      do_list: executive.assistant_guidelines?.do_list || [],
      dont_list: executive.assistant_guidelines?.dont_list || [],
      delegation_rules: executive.assistant_guidelines?.delegation_rules || '',
      
      // Notes
      assistant_visible_notes: executive.notes?.assistant_visible || '',
      admin_only_notes: executive.notes?.admin_only || ''
    });
  };

  // Executive form data state
  const [executiveFormData, setExecutiveFormData] = useState({
    full_name: '',
    executive_id: '',
    title: '',
    email: '',
    phone: '',
    work_hours: '',
    timezone: 'EST',
    preferred_contact_method: 'Email',
    travel_airlines: [],
    travel_seat_preference: 'aisle',
    travel_seat_cabin: 'economy',
    travel_seat_location: 'front',
    travel_hotels: [],
    travel_rental_car_class: 'midsize',
    travel_tsa_precheck: false,
    travel_global_entry: false,
    dietary_restrictions: [],
    allergies: [],
    beverages: [],
    favorite_cuisines: [],
    food_notes: '',
    meeting_platform: 'Google Meet',
    meeting_buffer: 10,
    meeting_recording: 'allowed',
    meeting_agenda_required: false,
    do_list: [],
    dont_list: [],
    delegation_rules: '',
    assistant_visible_notes: '',
    admin_only_notes: ''
  });

  const handleSaveExecutiveProfile = async () => {
    if (!selectedExecutive) return;
    
    setIsProcessing(true);
    try {
      const updateData = {
        full_name: executiveFormData.full_name,
        title: executiveFormData.title,
        email: executiveFormData.email,
        phone: executiveFormData.phone,
        work_hours: executiveFormData.work_hours,
        timezone: executiveFormData.timezone,
        preferred_contact_method: executiveFormData.preferred_contact_method,
        
        travel_preferences: {
          airlines: executiveFormData.travel_airlines,
          seat: {
            preference: executiveFormData.travel_seat_preference,
            cabin: executiveFormData.travel_seat_cabin,
            location: executiveFormData.travel_seat_location
          },
          hotels: {
            preferred_brands: executiveFormData.travel_hotels
          },
          rental_car: {
            class: executiveFormData.travel_rental_car_class
          },
          tsa_programs: {
            tsa_precheck: executiveFormData.travel_tsa_precheck,
            global_entry: executiveFormData.travel_global_entry
          }
        },
        
        food_beverage_preferences: {
          dietary_restrictions: executiveFormData.dietary_restrictions,
          allergies: executiveFormData.allergies,
          beverages: executiveFormData.beverages,
          favorite_cuisines: executiveFormData.favorite_cuisines,
          notes: executiveFormData.food_notes
        },
        
        meeting_preferences: {
          preferred_platform: executiveFormData.meeting_platform,
          buffer_minutes: executiveFormData.meeting_buffer,
          recording_policy: executiveFormData.meeting_recording,
          agenda_required: executiveFormData.meeting_agenda_required
        },
        
        assistant_guidelines: {
          do_list: executiveFormData.do_list,
          dont_list: executiveFormData.dont_list,
          delegation_rules: executiveFormData.delegation_rules
        },
        
        notes: {
          assistant_visible: executiveFormData.assistant_visible_notes,
          admin_only: executiveFormData.admin_only_notes
        }
      };

      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/executives/${selectedExecutive.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify(updateData)
      });

      if (res.ok) {
        success('Changes saved', 'Update is live.');
        fetchData();
      } else {
        showError('Couldn\'t save changes', 'Try again. If it keeps happening, refresh the page.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveAssistantProfile = async () => {
    if (!selectedAssistant) return;
    
    // Safeguard: Prevent accidental executive removal
    if (assistantFormData.executive_ids && assistantFormData.executive_ids.length === 0 && 
        selectedAssistant.executive_ids && selectedAssistant.executive_ids.length > 0) {
      const confirmed = window.confirm(
        'Warning: This will remove all executive assignments from this assistant. Are you sure?'
      );
      if (!confirmed) return;
    }
    
    setIsProcessing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({
          full_name: assistantFormData.full_name,
          email: assistantFormData.email,
          phone: assistantFormData.phone,
          is_active: assistantFormData.status === 'active',
          executive_ids: assistantFormData.executive_ids || []
        })
      });

      if (res.ok) {
        success('Changes saved', 'Update is live.');
        fetchData();
      } else {
        showError('Couldn\'t save changes', 'Try again. If it keeps happening, refresh the page.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedAssistant) return;
    
    setIsProcessing(true);
    try {
      // Generate a random password for the assistant
      const newPassword = Math.random().toString(36).slice(-12) + 'A1!';
      
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({
          new_password: newPassword
        })
      });

      if (res.ok) {
        const data = await res.json();
        // Store the password and show it in a persistent modal
        setGeneratedPassword(data.new_password || newPassword);
        setShowResetPasswordModal(false);
        setShowPasswordResultModal(true);
      } else {
        const errorData = await res.json().catch(() => ({}));
        showError('Password reset failed', errorData.detail || `Server returned ${res.status}`);
      }
    } catch (err) {
      showError('Connection issue', 'Unable to reset password. Please check your connection and try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredAssistants = assistants.filter(a => 
    a.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.employee_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredExecutives = executives.filter(e => 
    e.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', minHeight: '100vh', background: '#F8F9FA' }}>
        <p style={{ color: '#6B7280' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 32px' }}>
        <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              padding: '8px',
              background: 'transparent',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ArrowLeft size={18} color="#6B7280" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Admin Ops
            </h1>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              Manage assistants, executives, and what shows in their dashboards.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '32px', display: 'flex', gap: '24px', minHeight: 'calc(100vh - 100px)' }}>
        {/* LEFT SIDEBAR */}
        <div style={{ width: '320px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Tabs */}
          <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '8px', display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                setActiveTab('assistants');
                setSelectedAssistant(null);
                setSelectedExecutive(null);
              }}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: activeTab === 'assistants' ? '#3B82F6' : 'transparent',
                color: activeTab === 'assistants' ? '#FFFFFF' : '#6B7280',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Users size={16} />
              Assistants
            </button>
            <button
              onClick={() => {
                setActiveTab('executives');
                setSelectedAssistant(null);
                setSelectedExecutive(null);
              }}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: activeTab === 'executives' ? '#3B82F6' : 'transparent',
                color: activeTab === 'executives' ? '#FFFFFF' : '#6B7280',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <UserCog size={16} />
              Executives
            </button>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={18} color="#9CA3AF" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search by name, ID, or email…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#FFFFFF'
              }}
            />
          </div>

          {/* List */}
          <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', flex: 1, overflow: 'auto' }}>
            {activeTab === 'assistants' && (
              <>
                {filteredAssistants.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                    <Users size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
                    <p style={{ color: '#6B7280', fontSize: '14px' }}>No assistants found. Create one to get started.</p>
                  </div>
                ) : (
                  filteredAssistants.map((assistant) => (
                    <div
                      key={assistant.id}
                      onClick={() => handleAssistantSelect(assistant)}
                      style={{
                        padding: '16px',
                        borderBottom: '1px solid #F3F4F6',
                        cursor: 'pointer',
                        background: selectedAssistant?.id === assistant.id ? '#F9FAFB' : 'transparent'
                      }}
                    >
                      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>
                        {assistant.full_name}
                      </p>
                      <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 4px 0' }}>
                        {assistant.employee_id || 'No ID'} • {assistant.email}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500',
                          background: assistant.is_active ? '#DCFCE7' : '#FEE2E2',
                          color: assistant.is_active ? '#16A34A' : '#DC2626'
                        }}>
                          {assistant.is_active ? 'Active' : 'Disabled'}
                        </span>
                        <span style={{ color: '#9CA3AF', fontSize: '11px' }}>
                          {assistant.executives?.length || 0} exec{assistant.executives?.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === 'executives' && (
              <>
                {filteredExecutives.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                    <UserCog size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
                    <p style={{ color: '#6B7280', fontSize: '14px' }}>No executives found. Add an executive profile to assign assistants.</p>
                  </div>
                ) : (
                  filteredExecutives.map((executive) => (
                    <div
                      key={executive.id}
                      onClick={() => handleExecutiveSelect(executive)}
                      style={{
                        padding: '16px',
                        borderBottom: '1px solid #F3F4F6',
                        cursor: 'pointer',
                        background: selectedExecutive?.id === executive.id ? '#F9FAFB' : 'transparent'
                      }}
                    >
                      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>
                        {executive.full_name}
                      </p>
                      <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 4px 0' }}>
                        {executive.executive_id || 'No ID'} • {executive.title || 'No title'}
                      </p>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div style={{ flex: 1, background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '32px' }}>
          {!selectedAssistant && !selectedExecutive && (
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👈</div>
              <p style={{ color: '#6B7280', fontSize: '16px', margin: 0 }}>
                Select an {activeTab === 'assistants' ? 'assistant' : 'executive'} to manage their profile and content
              </p>
            </div>
          )}

          {/* ASSISTANT MANAGEMENT PANEL */}
          {selectedAssistant && (
            <div key={`assistant-${selectedAssistant.id}`}>
              <div style={{
                padding: '16px 20px',
                background: '#F9FAFB',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid #E5E7EB'
              }}>
                <h2 style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '600', margin: '0 0 6px 0' }}>
                  {selectedAssistant.full_name}
                </h2>
                <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                  <strong>ID:</strong> {selectedAssistant.id} • <strong>Email:</strong> {selectedAssistant.email} • <strong>Status:</strong> {selectedAssistant.is_active ? '✅ Active' : '❌ Disabled'}
                </p>
              </div>

              {/* Sub Tabs */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '0', flexWrap: 'wrap' }}>
                {[
                  { id: 'profile', label: 'Profile', icon: Edit },
                  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
                  { id: 'calendar', label: 'Calendar', icon: Calendar },
                  { id: 'travel', label: 'Travel', icon: Plane },
                  { id: 'messaging', label: 'Messaging', icon: MessageSquare },
                  { id: 'time', label: 'Time & Attendance', icon: UserCog }
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setAssistantSubTab(id)}
                    style={{
                      padding: '10px 16px',
                      background: 'transparent',
                      color: assistantSubTab === id ? '#3B82F6' : '#6B7280',
                      border: 'none',
                      borderBottom: assistantSubTab === id ? '2px solid #3B82F6' : '2px solid transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                ))}
              </div>

              {/* Profile Tab Content */}
              {assistantSubTab === 'profile' && (
                <div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Full Name
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Displayed across the assistant dashboard and messages.">ⓘ</span>
                      </label>
                      <input
                        type="text"
                        value={assistantFormData.full_name}
                        onChange={(e) => setAssistantFormData({ ...assistantFormData, full_name: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Assistant ID
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Automatically generated unique identifier. This cannot be changed.">ⓘ</span>
                      </label>
                      <input
                        type="text"
                        value={selectedAssistant.employee_id || 'Generating...'}
                        disabled
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          background: '#F0F9FF',
                          color: '#1E3A8A'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Email
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Used for login and notifications.">ⓘ</span>
                      </label>
                      <input
                        type="email"
                        value={assistantFormData.email}
                        onChange={(e) => setAssistantFormData({ ...assistantFormData, email: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Status
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Disabled assistants cannot log in. Use this for offboarding.">ⓘ</span>
                      </label>
                      <select
                        value={assistantFormData.status}
                        onChange={(e) => setAssistantFormData({ ...assistantFormData, status: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="active">Active</option>
                        <option value="disabled">Disabled</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Assigned Executive(s)
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Assistants can support up to 2 executives. They only see what's tied to those executives.">ⓘ</span>
                      </label>
                      <div style={{ border: '1px solid #D1D5DB', borderRadius: '8px', padding: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                        {executives.map((exec) => (
                          <label key={exec.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={assistantFormData.executive_ids.includes(exec.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  if (assistantFormData.executive_ids.length < 2) {
                                    setAssistantFormData({
                                      ...assistantFormData,
                                      executive_ids: [...assistantFormData.executive_ids, exec.id]
                                    });
                                  }
                                } else {
                                  setAssistantFormData({
                                    ...assistantFormData,
                                    executive_ids: assistantFormData.executive_ids.filter(id => id !== exec.id)
                                  });
                                }
                              }}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '14px', color: '#374151' }}>{exec.full_name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Onboarding Information (Read-Only) */}
                  {selectedAssistant.onboarding_data && (
                    <div style={{ marginTop: '32px', padding: '20px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px' }}>
                      <h3 style={{ color: '#059669', fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Onboarding Information
                      </h3>
                      <p style={{ color: '#166534', fontSize: '13px', marginBottom: '20px' }}>
                        Data collected during assistant onboarding. This information is read-only and can only be updated by the assistant.
                      </p>

                      {/* Availability & Working Hours */}
                      {selectedAssistant.onboarding_data.availability && (
                        <div style={{ marginBottom: '20px', padding: '16px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #D1FAE5' }}>
                          <h4 style={{ color: '#047857', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                            Availability & Working Hours
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                            {selectedAssistant.onboarding_data.availability.timezone && (
                              <div>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Timezone:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0' }}>{selectedAssistant.onboarding_data.availability.timezone}</p>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.availability.workingHours && (
                              <div>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Working Hours:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0' }}>{selectedAssistant.onboarding_data.availability.workingHours}</p>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.availability.daysAvailable && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Days Available:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0' }}>
                                  {Array.isArray(selectedAssistant.onboarding_data.availability.daysAvailable) 
                                    ? selectedAssistant.onboarding_data.availability.daysAvailable.join(', ')
                                    : selectedAssistant.onboarding_data.availability.daysAvailable}
                                </p>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.availability.emergencyAvailable && (
                              <div>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Emergency Availability:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0', textTransform: 'capitalize' }}>
                                  {selectedAssistant.onboarding_data.availability.emergencyAvailable}
                                </p>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.availability.availabilityNotes && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Notes:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                                  {selectedAssistant.onboarding_data.availability.availabilityNotes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Payment Information */}
                      {selectedAssistant.onboarding_data.payment_info && (
                        <div style={{ marginBottom: '20px', padding: '16px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #D1FAE5' }}>
                          <h4 style={{ color: '#047857', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                            Payment Preferences
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                            {selectedAssistant.onboarding_data.payment_info.paymentMethod && (
                              <div>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Payment Method:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0', textTransform: 'capitalize' }}>
                                  {selectedAssistant.onboarding_data.payment_info.paymentMethod.replace('_', ' ')}
                                </p>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.payment_info.legalName && (
                              <div>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Legal Name:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0' }}>{selectedAssistant.onboarding_data.payment_info.legalName}</p>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.payment_info.handle && (
                              <div>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Handle/Email:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0' }}>{selectedAssistant.onboarding_data.payment_info.handle}</p>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.payment_info.notes && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Notes:</span>
                                <p style={{ color: '#374151', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                                  {selectedAssistant.onboarding_data.payment_info.notes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Agreements Signed */}
                      {(selectedAssistant.onboarding_data.nda || selectedAssistant.onboarding_data.responsibilities) && (
                        <div style={{ padding: '16px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #D1FAE5' }}>
                          <h4 style={{ color: '#047857', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                            Signed Agreements
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                            {selectedAssistant.onboarding_data.nda?.ndaAgreed && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="#10B981">
                                  <path fillRule="evenodd" d="M8 16A8 8 0 108 0a8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L7 8.586 5.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span style={{ color: '#374151' }}>
                                  NDA signed by <strong>{selectedAssistant.onboarding_data.nda.legalName}</strong>
                                </span>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.responsibilities?.fullName && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="#10B981">
                                  <path fillRule="evenodd" d="M8 16A8 8 0 108 0a8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L7 8.586 5.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span style={{ color: '#374151' }}>
                                  Role Responsibilities acknowledged by <strong>{selectedAssistant.onboarding_data.responsibilities.fullName}</strong>
                                </span>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.security && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="#10B981">
                                  <path fillRule="evenodd" d="M8 16A8 8 0 108 0a8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L7 8.586 5.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span style={{ color: '#374151' }}>Security & Access Rules acknowledged</span>
                              </div>
                            )}
                            {selectedAssistant.onboarding_data.communication && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="#10B981">
                                  <path fillRule="evenodd" d="M8 16A8 8 0 108 0a8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L7 8.586 5.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span style={{ color: '#374151' }}>Communication Guidelines acknowledged</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
                    <button
                      onClick={handleSaveAssistantProfile}
                      disabled={isProcessing}
                      style={{
                        padding: '10px 20px',
                        background: '#3B82F6',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.6 : 1
                      }}
                    >
                      {isProcessing ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => setShowResetPasswordModal(true)}
                      style={{
                        padding: '10px 20px',
                        background: 'transparent',
                        color: '#6B7280',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Key size={16} />
                      Reset Password
                    </button>
                    <button
                      onClick={() => setShowDisableModal(true)}
                      style={{
                        padding: '10px 20px',
                        background: 'transparent',
                        color: '#DC2626',
                        border: '1px solid #FCA5A5',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Power size={16} />
                      {assistantFormData.status === 'active' ? 'Disable Assistant' : 'Enable Assistant'}
                    </button>
                  </div>
                </div>
              )}

              {/* Tasks Tab */}
              {assistantSubTab === 'tasks' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                      <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                        These tasks appear on the assistant's board immediately.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAddTaskModal(true)}
                      style={{
                        padding: '8px 16px',
                        background: '#3B82F6',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Plus size={16} />
                      Add Task
                    </button>
                  </div>

                  {tasks.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                      <CheckSquare size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
                      <p style={{ color: '#6B7280', fontSize: '14px' }}>
                        No tasks yet. Add one to populate the assistant's To-Do Center.
                      </p>
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#F9FAFB' }}>
                          <tr>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Task</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Bucket</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Priority</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Due Date</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tasks.map((task, idx) => (
                            <tr key={task.id} style={{ borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none' }}>
                              <td style={{ padding: '16px' }}>
                                <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>
                                  {task.title}
                                </p>
                                {task.description && (
                                  <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                                    {task.description}
                                  </p>
                                )}
                              </td>
                              <td style={{ padding: '16px' }}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  background: '#F3F4F6',
                                  color: '#6B7280'
                                }}>
                                  {task.bucket || 'today'}
                                </span>
                              </td>
                              <td style={{ padding: '16px' }}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  background: task.priority === 'high' ? '#FEE2E2' : task.priority === 'medium' ? '#FEF3C7' : '#F3F4F6',
                                  color: task.priority === 'high' ? '#DC2626' : task.priority === 'medium' ? '#D97706' : '#6B7280'
                                }}>
                                  {task.priority || 'medium'}
                                </span>
                              </td>
                              <td style={{ padding: '16px' }}>
                                <span style={{ color: '#6B7280', fontSize: '13px' }}>
                                  {task.due_date ? new Date(task.due_date).toLocaleDateString() : '—'}
                                </span>
                              </td>
                              <td style={{ padding: '16px', textAlign: 'right' }}>
                                <button
                                  onClick={() => handleDeleteTask(task.id)}
                                  style={{
                                    padding: '4px 8px',
                                    background: 'transparent',
                                    border: '1px solid #FCA5A5',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    color: '#DC2626'
                                  }}
                                  title="Delete task"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Calendar Tab */}
              {assistantSubTab === 'calendar' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                      <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                        Events added here appear on the assistant's calendar immediately.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAddEventModal(true)}
                      style={{
                        padding: '8px 16px',
                        background: '#3B82F6',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Plus size={16} />
                      Add Event
                    </button>
                  </div>

                  {events.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                      <Calendar size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
                      <p style={{ color: '#6B7280', fontSize: '14px' }}>
                        Agenda shows a list of upcoming events. Month shows the calendar grid.
                      </p>
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#F9FAFB' }}>
                          <tr>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Event</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Date & Time</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Location</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {events.map((event, idx) => (
                            <tr key={event.id} style={{ borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none' }}>
                              <td style={{ padding: '16px' }}>
                                <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>
                                  {event.title}
                                </p>
                                {event.notes && (
                                  <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                                    {event.notes}
                                  </p>
                                )}
                              </td>
                              <td style={{ padding: '16px' }}>
                                <p style={{ color: '#374151', fontSize: '13px', margin: '0 0 2px 0' }}>
                                  {event.start_datetime ? new Date(event.start_datetime).toLocaleString() : 'N/A'}
                                </p>
                                {event.end_datetime && (
                                  <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                                    to {new Date(event.end_datetime).toLocaleString()}
                                  </p>
                                )}
                              </td>
                              <td style={{ padding: '16px' }}>
                                <span style={{ color: '#6B7280', fontSize: '13px' }}>
                                  {event.location?.type || 'Not specified'}
                                </span>
                              </td>
                              <td style={{ padding: '16px', textAlign: 'right' }}>
                                <button
                                  onClick={() => handleDeleteEvent(event.id)}
                                  style={{
                                    padding: '4px 8px',
                                    background: 'transparent',
                                    border: '1px solid #FCA5A5',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    color: '#DC2626'
                                  }}
                                  title="Delete event"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Travel Tab */}
              {assistantSubTab === 'travel' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                      <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                        Store travel details, confirmations, and receipts for this assistant/executive.
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => navigate('/admin/travel-archive')}
                        style={{
                          padding: '8px 16px',
                          background: 'transparent',
                          color: '#6B7280',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        title="Archived items are hidden from active views but remain searchable."
                      >
                        <Archive size={16} />
                        View Archive
                      </button>
                      <button
                        onClick={() => setShowAddTravelModal(true)}
                        style={{
                          padding: '8px 16px',
                          background: '#3B82F6',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Plus size={16} />
                        Add Travel Item
                      </button>
                    </div>
                  </div>

                  {travelItems.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                      <Plane size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
                      <p style={{ color: '#6B7280', fontSize: '14px' }}>
                        No travel items yet. Add flights, hotels, receipts, or itinerary notes.
                      </p>
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#F9FAFB' }}>
                          <tr>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Trip</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Type</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Vendor</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Confirmation</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {travelItems.map((item, idx) => (
                            <tr key={item.id} style={{ borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none' }}>
                              <td style={{ padding: '16px' }}>
                                <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>
                                  {item.trip_name}
                                </p>
                                {item.travel_date && (
                                  <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                                    {new Date(item.travel_date).toLocaleDateString()}
                                  </p>
                                )}
                              </td>
                              <td style={{ padding: '16px' }}>
                                <span style={{ color: '#374151', fontSize: '13px' }}>
                                  {item.type || 'N/A'}
                                </span>
                              </td>
                              <td style={{ padding: '16px' }}>
                                <span style={{ color: '#6B7280', fontSize: '13px' }}>
                                  {item.vendor || '—'}
                                </span>
                              </td>
                              <td style={{ padding: '16px' }}>
                                <span style={{ color: '#6B7280', fontSize: '13px', fontFamily: 'monospace' }}>
                                  {item.confirmation || '—'}
                                </span>
                              </td>
                              <td style={{ padding: '16px', textAlign: 'right' }}>
                                <button
                                  onClick={() => handleDeleteTravel(item.id)}
                                  style={{
                                    padding: '4px 8px',
                                    background: 'transparent',
                                    border: '1px solid #FCA5A5',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    color: '#DC2626'
                                  }}
                                  title="Delete travel item"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Messages Tab - Read-only */}
              {assistantSubTab === 'messages' && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                      Read-only view of assistant ↔ executive message threads. For oversight, not participation.
                    </p>
                  </div>

                  <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                    <MessageSquare size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
                    <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '8px' }}>
                      Message oversight feature
                    </p>
                    <p style={{ color: '#9CA3AF', fontSize: '13px' }}>
                      This feature allows admins to view conversation threads between assistants and executives for quality assurance and oversight purposes.
                    </p>
                    <div style={{ marginTop: '20px', padding: '16px', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      <p style={{ color: '#374151', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '500' }}>
                        📋 What you can do:
                      </p>
                      <ul style={{ color: '#6B7280', fontSize: '13px', margin: 0, paddingLeft: '20px', textAlign: 'left' }}>
                        <li>View all message threads for this assistant</li>
                        <li>Read conversation history with executives</li>
                        <li>Monitor communication quality</li>
                        <li>Optional: Send system notes as Admin (if enabled)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Time & Attendance Tab */}
              {assistantSubTab === 'time' && (
                <TimeAttendanceTab assistantId={selectedAssistant.id} assistantName={selectedAssistant.full_name} />
              )}

              {/* Messaging Tab */}
              {assistantSubTab === 'messaging' && (
                <MessagingTab assistant={selectedAssistant} />
              )}
            </div>
          )}

          {/* EXECUTIVE MANAGEMENT PANEL */}
          {selectedExecutive && (
            <div>
              <h2 style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '600', margin: '0 0 8px 0' }}>
                {selectedExecutive.full_name}
              </h2>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 24px 0' }}>
                This information is visible to assistants. Keep it clear and actionable.
              </p>

              {/* Sub Tabs */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '0' }}>
                {[
                  { id: 'profile', label: 'Profile' },
                  { id: 'preferences', label: 'Preferences' }
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => setExecutiveSubTab(id)}
                    style={{
                      padding: '10px 16px',
                      background: 'transparent',
                      color: executiveSubTab === id ? '#3B82F6' : '#6B7280',
                      border: 'none',
                      borderBottom: executiveSubTab === id ? '2px solid #3B82F6' : '2px solid transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Profile Tab */}
              {executiveSubTab === 'profile' && (
                <div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={executiveFormData.full_name}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, full_name: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Executive ID
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Automatically generated unique identifier. This cannot be changed.">ⓘ</span>
                      </label>
                      <input
                        type="text"
                        value={selectedExecutive.executive_id || 'Generating...'}
                        disabled
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          background: '#FEF3C7',
                          color: '#78350F'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Title / Role
                      </label>
                      <input
                        type="text"
                        value={executiveFormData.title}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, title: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={executiveFormData.email}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, email: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={executiveFormData.phone}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, phone: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Work Hours
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Assistants use this to schedule meetings and plan daily briefings.">ⓘ</span>
                      </label>
                      <input
                        type="text"
                        value={executiveFormData.work_hours}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, work_hours: e.target.value })}
                        placeholder="Mon–Fri, 9am–5pm EST"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Timezone
                      </label>
                      <select
                        value={executiveFormData.timezone}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, timezone: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="EST">EST (Eastern)</option>
                        <option value="CST">CST (Central)</option>
                        <option value="MST">MST (Mountain)</option>
                        <option value="PST">PST (Pacific)</option>
                        <option value="GMT">GMT (Greenwich)</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Preferred Contact Method
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Defines how the executive prefers to communicate and what counts as urgent.">ⓘ</span>
                      </label>
                      <select
                        value={executiveFormData.preferred_contact_method}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, preferred_contact_method: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="Email">Email</option>
                        <option value="Phone">Phone</option>
                        <option value="Text">Text</option>
                        <option value="Slack">Slack</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Notes (Assistant visible)
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Short operational notes the assistant should follow daily.">ⓘ</span>
                      </label>
                      <textarea
                        value={executiveFormData.assistant_visible_notes}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, assistant_visible_notes: e.target.value })}
                        placeholder="Operational notes visible to assistant..."
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Notes (Admin only)
                        <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Sensitive or internal-only notes—never shown to assistants.">ⓘ</span>
                      </label>
                      <textarea
                        value={executiveFormData.admin_only_notes}
                        onChange={(e) => setExecutiveFormData({ ...executiveFormData, admin_only_notes: e.target.value })}
                        placeholder="Internal notes (assistants never see this)..."
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #FEF3C7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          background: '#FFFBEB'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
                    <button
                      onClick={handleSaveExecutiveProfile}
                      disabled={isProcessing}
                      style={{
                        padding: '10px 20px',
                        background: '#3B82F6',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.6 : 1
                      }}
                    >
                      {isProcessing ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* Preferences Tab */}
              {executiveSubTab === 'preferences' && (
                <div style={{ maxHeight: '600px', overflowY: 'auto', paddingRight: '16px' }}>
                  {/* Travel Preferences */}
                  <div style={{ marginBottom: '32px', padding: '20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                    <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0' }}>
                      Travel Preferences
                    </h3>
                    <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '0 0 16px 0' }}>Used for flights, hotels, and trip planning.</p>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          Seat Preference
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {['aisle', 'window'].map(pref => (
                            <button
                              key={pref}
                              onClick={() => setExecutiveFormData({ ...executiveFormData, travel_seat_preference: pref })}
                              style={{
                                flex: 1,
                                padding: '8px',
                                background: executiveFormData.travel_seat_preference === pref ? '#3B82F6' : '#F9FAFB',
                                color: executiveFormData.travel_seat_preference === pref ? '#FFFFFF' : '#6B7280',
                                border: '1px solid ' + (executiveFormData.travel_seat_preference === pref ? '#3B82F6' : '#E5E7EB'),
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                textTransform: 'capitalize'
                              }}
                            >
                              {pref}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          Cabin Class
                        </label>
                        <select
                          value={executiveFormData.travel_seat_cabin}
                          onChange={(e) => setExecutiveFormData({ ...executiveFormData, travel_seat_cabin: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        >
                          <option value="economy">Economy</option>
                          <option value="comfort">Comfort+</option>
                          <option value="business">Business</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={executiveFormData.travel_tsa_precheck}
                            onChange={(e) => setExecutiveFormData({ ...executiveFormData, travel_tsa_precheck: e.target.checked })}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '13px', color: '#374151' }}>TSA PreCheck</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Food & Beverage */}
                  <div style={{ marginBottom: '32px', padding: '20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                    <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0' }}>
                      Food & Beverage
                    </h3>
                    <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '0 0 16px 0' }}>Optional, but extremely useful for travel and meeting prep.</p>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          Dietary Restrictions (comma separated)
                        </label>
                        <input
                          type="text"
                          value={executiveFormData.dietary_restrictions.join(', ')}
                          onChange={(e) => setExecutiveFormData({ 
                            ...executiveFormData, 
                            dietary_restrictions: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          })}
                          placeholder="e.g., no pork, vegetarian"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          Favorite Beverages
                        </label>
                        <input
                          type="text"
                          value={executiveFormData.beverages.join(', ')}
                          onChange={(e) => setExecutiveFormData({ 
                            ...executiveFormData, 
                            beverages: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          })}
                          placeholder="e.g., black coffee, sparkling water"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Meeting Preferences */}
                  <div style={{ marginBottom: '32px', padding: '20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                    <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0' }}>
                      Meeting Preferences
                    </h3>
                    <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '0 0 16px 0' }}>Controls meeting buffers, platforms, agenda requirements, and recording policy.</p>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          Preferred Platform
                        </label>
                        <select
                          value={executiveFormData.meeting_platform}
                          onChange={(e) => setExecutiveFormData({ ...executiveFormData, meeting_platform: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        >
                          <option value="Google Meet">Google Meet</option>
                          <option value="Zoom">Zoom</option>
                          <option value="Phone">Phone</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          Buffer Between Meetings (minutes)
                        </label>
                        <input
                          type="number"
                          value={executiveFormData.meeting_buffer}
                          onChange={(e) => setExecutiveFormData({ ...executiveFormData, meeting_buffer: parseInt(e.target.value) || 0 })}
                          min="0"
                          max="60"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={executiveFormData.meeting_agenda_required}
                            onChange={(e) => setExecutiveFormData({ ...executiveFormData, meeting_agenda_required: e.target.checked })}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '13px', color: '#374151' }}>Agenda Required</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Assistant Guidelines */}
                  <div style={{ marginBottom: '32px', padding: '20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                    <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0' }}>
                      Assistant Guidelines
                    </h3>
                    <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '0 0 16px 0' }}>Put your 'do/don't' rules here so assistants don't have to guess.</p>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          DO List (one per line)
                        </label>
                        <textarea
                          value={executiveFormData.do_list.join('\n')}
                          onChange={(e) => setExecutiveFormData({ 
                            ...executiveFormData, 
                            do_list: e.target.value.split('\n').filter(Boolean)
                          })}
                          placeholder="Send daily briefing by 9:00 AM&#10;Confirm meeting links in invites"
                          rows={4}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          DON'T List (one per line)
                        </label>
                        <textarea
                          value={executiveFormData.dont_list.join('\n')}
                          onChange={(e) => setExecutiveFormData({ 
                            ...executiveFormData, 
                            dont_list: e.target.value.split('\n').filter(Boolean)
                          })}
                          placeholder="Do not book meetings after 5 PM&#10;Do not share phone number externally"
                          rows={4}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                          Delegation Rules
                        </label>
                        <textarea
                          value={executiveFormData.delegation_rules}
                          onChange={(e) => setExecutiveFormData({ ...executiveFormData, delegation_rules: e.target.value })}
                          placeholder="Can schedule internal meetings without approval; external requires approval."
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', paddingTop: '24px', borderTop: '1px solid #E5E7EB', position: 'sticky', bottom: 0, background: '#FFFFFF' }}>
                    <button
                      onClick={handleSaveExecutiveProfile}
                      disabled={isProcessing}
                      style={{
                        padding: '10px 20px',
                        background: '#3B82F6',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.6 : 1
                      }}
                    >
                      {isProcessing ? 'Saving...' : 'Save Preferences'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modals */}
      <ConfirmModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        onConfirm={handleResetPassword}
        title="Reset assistant password?"
        message="This will invalidate the current password. The assistant will be prompted to set a new one on next login."
        confirmText="Reset Password"
        cancelText="Cancel"
        isLoading={isProcessing}
      />

      {/* Password Result Modal - Stays visible until user closes it */}
      {showPasswordResultModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '450px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#DCFCE7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Check size={20} color="#16A34A" />
                </div>
                <h3 style={{ 
                  margin: 0, 
                  color: '#0A0A0A', 
                  fontSize: '18px', 
                  fontWeight: '600' 
                }}>
                  Password Reset Successful
                </h3>
              </div>
              <p style={{ 
                color: '#6B7280', 
                fontSize: '14px', 
                margin: '12px 0 0 0',
                lineHeight: '1.5'
              }}>
                The new password for <strong>{selectedAssistant?.full_name}</strong> is shown below. 
                Copy it now — it will not be shown again.
              </p>
            </div>

            <div style={{
              background: '#F3F4F6',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <label style={{
                display: 'block',
                color: '#6B7280',
                fontSize: '12px',
                fontWeight: '500',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                New Password
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <code style={{
                  flex: 1,
                  background: '#FFFFFF',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  padding: '12px 14px',
                  fontSize: '16px',
                  fontFamily: 'monospace',
                  color: '#0A0A0A',
                  letterSpacing: '0.5px',
                  wordBreak: 'break-all'
                }}>
                  {generatedPassword}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedPassword);
                    success('Copied!', 'Password copied to clipboard');
                  }}
                  style={{
                    padding: '12px 16px',
                    background: '#3B82F6',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background 0.15s',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#2563EB'}
                  onMouseOut={(e) => e.target.style.background = '#3B82F6'}
                >
                  Copy
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setShowPasswordResultModal(false);
                setGeneratedPassword('');
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: '#F3F4F6',
                color: '#374151',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#E5E7EB';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F3F4F6';
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onConfirm={() => {
          setAssistantFormData({
            ...assistantFormData,
            status: assistantFormData.status === 'active' ? 'disabled' : 'active'
          });
          setShowDisableModal(false);
          handleSaveAssistantProfile();
        }}
        title={`${assistantFormData.status === 'active' ? 'Disable' : 'Enable'} assistant access?`}
        message={assistantFormData.status === 'active' 
          ? "They will not be able to log in until re-enabled."
          : "They will be able to log in again."}
        confirmText={assistantFormData.status === 'active' ? 'Disable' : 'Enable'}
        cancelText="Cancel"
        isLoading={isProcessing}
      />

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
              Add Task
            </h3>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 24px 0' }}>
              Create a task directly into any column.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Task Title *
                </label>
                <input
                  type="text"
                  value={taskFormData.title}
                  onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                  placeholder="e.g., Review Q4 budget"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Description
                </label>
                <textarea
                  value={taskFormData.description}
                  onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                  placeholder="Add context or steps..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Bucket
                  <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Use columns for workflow.">ⓘ</span>
                </label>
                <select
                  value={taskFormData.bucket}
                  onChange={(e) => setTaskFormData({ ...taskFormData, bucket: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="today">Today</option>
                  <option value="tomorrow">Tomorrow</option>
                  <option value="this_week">This Week</option>
                  <option value="next_week">Next Week</option>
                  <option value="backlog">Backlog</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Priority
                </label>
                <select
                  value={taskFormData.priority}
                  onChange={(e) => setTaskFormData({ ...taskFormData, priority: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Due Date
                  <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Use due dates for deadlines.">ⓘ</span>
                </label>
                <input
                  type="date"
                  value={taskFormData.due_date}
                  onChange={(e) => setTaskFormData({ ...taskFormData, due_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Related Executive (optional)
                </label>
                <select
                  value={taskFormData.executive_id}
                  onChange={(e) => setTaskFormData({ ...taskFormData, executive_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">None</option>
                  {executives.map(exec => (
                    <option key={exec.id} value={exec.id}>{exec.full_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleAddTask}
                disabled={isProcessing || !taskFormData.title}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isProcessing || !taskFormData.title ? 'not-allowed' : 'pointer',
                  opacity: isProcessing || !taskFormData.title ? 0.6 : 1
                }}
              >
                {isProcessing ? 'Adding...' : 'Add Task'}
              </button>
              <button
                onClick={() => {
                  setShowAddTaskModal(false);
                  setTaskFormData({
                    title: '',
                    description: '',
                    bucket: 'today',
                    priority: 'medium',
                    due_date: '',
                    executive_id: ''
                  });
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEventModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
              Add Calendar Event
            </h3>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 24px 0' }}>
              Event will appear on assistant's calendar immediately.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Event Title *
                </label>
                <input
                  type="text"
                  value={eventFormData.title}
                  onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
                  placeholder="e.g., Team Meeting"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={eventFormData.start_datetime}
                  onChange={(e) => setEventFormData({ ...eventFormData, start_datetime: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={eventFormData.end_datetime}
                  onChange={(e) => setEventFormData({ ...eventFormData, end_datetime: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Location Type
                  <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Choose a common type. Use 'In Person' to provide an address.">ⓘ</span>
                </label>
                <select
                  value={eventFormData.location_type}
                  onChange={(e) => setEventFormData({ ...eventFormData, location_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="Google Meet">Google Meet</option>
                  <option value="Zoom">Zoom</option>
                  <option value="Phone">Phone</option>
                  <option value="In Person">In Person</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Link / Address
                  <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }} title="Add a link for virtual meetings or an address for in-person events.">ⓘ</span>
                </label>
                <input
                  type="text"
                  value={eventFormData.location_type === 'In Person' ? eventFormData.location_address : eventFormData.location_link}
                  onChange={(e) => setEventFormData({ 
                    ...eventFormData, 
                    [eventFormData.location_type === 'In Person' ? 'location_address' : 'location_link']: e.target.value 
                  })}
                  placeholder={eventFormData.location_type === 'In Person' ? '123 Main St, City, State' : 'https://...'}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Notes / Agenda
                </label>
                <textarea
                  value={eventFormData.notes}
                  onChange={(e) => setEventFormData({ ...eventFormData, notes: e.target.value })}
                  placeholder="Meeting agenda or notes..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleAddEvent}
                disabled={isProcessing || !eventFormData.title || !eventFormData.start_datetime}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isProcessing || !eventFormData.title || !eventFormData.start_datetime ? 'not-allowed' : 'pointer',
                  opacity: isProcessing || !eventFormData.title || !eventFormData.start_datetime ? 0.6 : 1
                }}
              >
                {isProcessing ? 'Adding...' : 'Add Event'}
              </button>
              <button
                onClick={() => {
                  setShowAddEventModal(false);
                  setEventFormData({
                    title: '',
                    start_datetime: '',
                    end_datetime: '',
                    location_type: 'Google Meet',
                    location_link: '',
                    location_address: '',
                    notes: '',
                    category: 'executive'
                  });
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Travel Modal */}
      {showAddTravelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
              Add Travel Item
            </h3>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 24px 0' }}>
              Add flights, hotels, receipts, or itinerary notes.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Trip Name *
                </label>
                <input
                  type="text"
                  value={travelFormData.trip_name}
                  onChange={(e) => setTravelFormData({ ...travelFormData, trip_name: e.target.value })}
                  placeholder="e.g., NYC Business Trip"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Type
                </label>
                <select
                  value={travelFormData.type}
                  onChange={(e) => setTravelFormData({ ...travelFormData, type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="flight">Flight</option>
                  <option value="hotel">Hotel</option>
                  <option value="car">Car Rental</option>
                  <option value="itinerary">Itinerary</option>
                  <option value="receipt">Receipt</option>
                  <option value="note">Note</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Vendor / Provider
                </label>
                <input
                  type="text"
                  value={travelFormData.vendor}
                  onChange={(e) => setTravelFormData({ ...travelFormData, vendor: e.target.value })}
                  placeholder="e.g., Delta, Marriott"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Confirmation Number
                </label>
                <input
                  type="text"
                  value={travelFormData.confirmation}
                  onChange={(e) => setTravelFormData({ ...travelFormData, confirmation: e.target.value })}
                  placeholder="ABC123"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Travel Date
                </label>
                <input
                  type="date"
                  value={travelFormData.travel_date}
                  onChange={(e) => setTravelFormData({ ...travelFormData, travel_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Notes
                </label>
                <textarea
                  value={travelFormData.notes}
                  onChange={(e) => setTravelFormData({ ...travelFormData, notes: e.target.value })}
                  placeholder="Additional details..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleAddTravel}
                disabled={isProcessing || !travelFormData.trip_name}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isProcessing || !travelFormData.trip_name ? 'not-allowed' : 'pointer',
                  opacity: isProcessing || !travelFormData.trip_name ? 0.6 : 1
                }}
              >
                {isProcessing ? 'Adding...' : 'Add Travel Item'}
              </button>
              <button
                onClick={() => {
                  setShowAddTravelModal(false);
                  setTravelFormData({
                    trip_name: '',
                    type: 'flight',
                    vendor: '',
                    confirmation: '',
                    travel_date: '',
                    notes: ''
                  });
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Time & Attendance Tab Component
const TimeAttendanceTab = ({ assistantId, assistantName }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | approved | rejected
  const { success, error: showError } = useToast();

  useEffect(() => {
    fetchTimeEntries();
  }, [assistantId]);

  const fetchTimeEntries = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/time-clock/admin/entries?assistant_id=${assistantId}`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTimeEntries(data.entries || []);
      } else {
        showError('Failed to load entries', 'Please try again');
      }
    } catch (err) {
      console.error('Error fetching time entries:', err);
      showError('Network Error', 'Could not load time entries');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (entryId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/time-clock/admin/entries/${entryId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ notes: 'Approved by admin' })
      });

      if (res.ok) {
        success('Approved', 'Time entry has been approved');
        fetchTimeEntries();
      } else {
        showError('Failed to approve', 'Please try again');
      }
    } catch (err) {
      showError('Network Error', 'Could not approve entry');
    }
  };

  const handleReject = async (entryId) => {
    const reason = window.prompt('Reason for rejection (will be sent to assistant):');
    if (!reason) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/time-clock/admin/entries/${entryId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ notes: reason })
      });

      if (res.ok) {
        success('Rejected', 'Time entry has been rejected');
        fetchTimeEntries();
      } else {
        showError('Failed to reject', 'Please try again');
      }
    } catch (err) {
      showError('Network Error', 'Could not reject entry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatHours = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const filteredEntries = timeEntries.filter(entry => {
    if (filter === 'all') return true;
    if (filter === 'pending') return entry.status === 'pending';
    if (filter === 'approved') return entry.status === 'approved';
    if (filter === 'rejected') return entry.status === 'rejected';
    return true;
  });

  const pendingCount = timeEntries.filter(e => e.status === 'pending').length;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 16px 0' }}>
          Review and approve manual time entries for {assistantName}. All clock in/out entries are automatically recorded.
        </p>

        {/* Filter Buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: 'All Entries', count: timeEntries.length },
            { id: 'pending', label: 'Pending', count: pendingCount },
            { id: 'approved', label: 'Approved', count: timeEntries.filter(e => e.status === 'approved').length },
            { id: 'rejected', label: 'Rejected', count: timeEntries.filter(e => e.status === 'rejected').length }
          ].map(({ id, label, count }) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              style={{
                padding: '8px 14px',
                background: filter === id ? '#EFF6FF' : 'transparent',
                color: filter === id ? '#3B82F6' : '#6B7280',
                border: `1px solid ${filter === id ? '#3B82F6' : '#D1D5DB'}`,
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {label}
              <span style={{
                padding: '2px 6px',
                background: filter === id ? '#3B82F6' : '#E5E7EB',
                color: filter === id ? '#FFFFFF' : '#6B7280',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                {count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
          Loading time entries...
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredEntries.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
          <Clock size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
            {filter === 'all' ? 'No time entries yet' : `No ${filter} entries`}
          </p>
        </div>
      )}

      {/* Time Entries Table */}
      {!loading && filteredEntries.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                  Date
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                  Time In
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                  Time Out
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                  Total
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                  Type
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                  Status
                </th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                  Reason
                </th>
                <th style={{ padding: '12px', textAlign: 'center', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <tr key={entry.entry_id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '12px', color: '#0A0A0A', fontSize: '13px' }}>
                    {formatDate(entry.requested_date || entry.clock_in_time)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px' }}>
                    {formatTime(entry.start_time || entry.clock_in_time)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '13px' }}>
                    {formatTime(entry.end_time || entry.clock_out_time)}
                  </td>
                  <td style={{ padding: '12px', color: '#0A0A0A', fontSize: '13px', fontWeight: '500' }}>
                    {formatHours(entry.total_duration || entry.total_hours)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: entry.is_manual ? '#FEF3C7' : '#DCFCE7',
                      color: entry.is_manual ? '#92400E' : '#166534'
                    }}>
                      {entry.is_manual ? 'Manual' : 'Auto'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: 
                        entry.status === 'approved' ? '#DCFCE7' :
                        entry.status === 'rejected' ? '#FEE2E2' :
                        '#FEF3C7',
                      color:
                        entry.status === 'approved' ? '#166534' :
                        entry.status === 'rejected' ? '#991B1B' :
                        '#92400E'
                    }}>
                      {entry.status || 'N/A'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px', maxWidth: '200px' }}>
                    {entry.reason || '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {entry.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleApprove(entry.entry_id)}
                          style={{
                            padding: '6px 10px',
                            background: '#10B981',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Check size={14} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(entry.entry_id)}
                          style={{
                            padding: '6px 10px',
                            background: '#EF4444',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <X size={14} />
                          Reject
                        </button>
                      </div>
                    )}
                    {entry.status !== 'pending' && (
                      <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                        {entry.status === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Stats */}
      {!loading && timeEntries.length > 0 && (
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: '#F9FAFB',
          borderRadius: '8px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px'
        }}>
          <div>
            <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 4px 0' }}>Total Hours</p>
            <p style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '700', margin: 0 }}>
              {formatHours(timeEntries.reduce((sum, e) => sum + (e.total_duration || e.total_hours || 0), 0))}
            </p>
          </div>
          <div>
            <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 4px 0' }}>Pending Approvals</p>
            <p style={{ color: '#F59E0B', fontSize: '20px', fontWeight: '700', margin: 0 }}>
              {pendingCount}
            </p>
          </div>
          <div>
            <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 4px 0' }}>Manual Entries</p>
            <p style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '700', margin: 0 }}>
              {timeEntries.filter(e => e.is_manual).length}
            </p>
          </div>
        </div>
      )}

      {/* HR/Payroll Messages Panel */}
      <HRPayrollMessagesPanel assistantId={assistantId} assistantName={assistantName} />
    </div>
  );
};

// HR/Payroll Messages Panel Component
const HRPayrollMessagesPanel = ({ assistantId, assistantName }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { success, error: showError } = useToast();

  useEffect(() => {
    fetchMessages();
  }, [assistantId]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/time-clock/admin/hr-messages/${assistantId}`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching HR messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/time-clock/admin/hr-messages/${assistantId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ content: newMessage })
      });

      if (res.ok) {
        success('Sent', 'Message sent to assistant');
        setNewMessage('');
        fetchMessages();
      } else {
        showError('Failed to send', 'Please try again');
      }
    } catch (err) {
      showError('Network Error', 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ marginTop: '32px' }}>
      <div style={{
        padding: '16px 20px',
        background: '#F9FAFB',
        borderRadius: '8px 8px 0 0',
        borderBottom: '1px solid #E5E7EB'
      }}>
        <h4 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '600', margin: '0 0 4px 0' }}>
          💼 HR / Payroll Messages
        </h4>
        <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
          Communication thread for time entry approvals/rejections with {assistantName}
        </p>
      </div>

      {/* Messages List */}
      <div style={{
        border: '1px solid #E5E7EB',
        borderTop: 'none',
        maxHeight: '400px',
        overflowY: 'auto',
        background: '#FFFFFF'
      }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            Loading messages...
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF', fontSize: '14px' }}>
            No HR/Payroll messages yet. Messages will appear here when time entries are approved or rejected.
          </div>
        )}

        {!loading && messages.length > 0 && (
          <div style={{ padding: '16px' }}>
            {messages.map((msg, index) => (
              <div
                key={msg.id || index}
                style={{
                  marginBottom: '16px',
                  padding: '12px 16px',
                  background: msg.sender_type === 'system' && msg.sender_id !== 'admin' ? '#F0F9FF' : '#F9FAFB',
                  borderRadius: '8px',
                  borderLeft: `3px solid ${msg.sender_type === 'system' && msg.sender_id !== 'admin' ? '#3B82F6' : '#10B981'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#0A0A0A', fontSize: '13px', fontWeight: '600' }}>
                    {msg.sender_name || (msg.sender_type === 'assistant' ? assistantName : 'HR - Admin')}
                  </span>
                  <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                    {formatTimestamp(msg.created_at)}
                  </span>
                </div>
                <p style={{ color: '#374151', fontSize: '13px', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reply Form */}
      <div style={{
        padding: '16px',
        background: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px'
      }}>
        <form onSubmit={handleSendReply}>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message to the assistant..."
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              marginBottom: '12px'
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              style={{
                padding: '10px 20px',
                background: sending || !newMessage.trim() ? '#D1D5DB' : '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: sending || !newMessage.trim() ? 'not-allowed' : 'pointer'
              }}
            >
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Messaging Tab Component
const MessagingTab = ({ assistant }) => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { success, error: showError } = useToast();
  
  // File attachment state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const MAX_FILES = 3;

  // Log assistant data for debugging
  useEffect(() => {
    console.log('📋 Messaging Tab - Assistant:', {
      id: assistant?.id,
      name: assistant?.full_name,
      executive_ids: assistant?.executive_ids,
      executives: assistant?.executives
    });
  }, [assistant]);

  useEffect(() => {
    if (assistant?.id) {
      fetchThreads();
    }
  }, [assistant?.id]);

  useEffect(() => {
    if (selectedThread && assistant?.id) {
      fetchMessages();
      // Poll for new messages
      const interval = setInterval(() => fetchMessages(), 10000);
      return () => clearInterval(interval);
    }
  }, [selectedThread, assistant?.id]);

  useEffect(() => {
    // Auto-scroll to bottom when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchThreads = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${BACKEND_URL}/api/assistants/${assistant.id}/threads`;
      console.log('📡 Fetching threads from:', url);
      
      const res = await fetch(url, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      
      console.log('📡 Threads response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('📨 Threads data:', data);
        setThreads(data.threads || []);
        // Auto-select first thread if available
        if (data.threads && data.threads.length > 0 && !selectedThread) {
          setSelectedThread(data.threads[0]);
        }
      } else {
        const errorText = await res.text();
        console.error('❌ Threads fetch failed:', res.status, errorText);
        setError(`Failed to load threads (${res.status})`);
      }
    } catch (err) {
      console.error('❌ Error fetching threads:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedThread || !assistant?.id) return;
    
    try {
      const url = `${BACKEND_URL}/api/assistants/${assistant.id}/messages?executive_id=${selectedThread.executive_id}`;
      console.log('📡 Fetching messages from:', url);
      
      const res = await fetch(url, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      
      console.log('📡 Messages response status:', res.status);
      
      if (res.ok) {
        const messages = await res.json();
        console.log('📨 Messages:', messages);
        setMessages(messages || []);
      } else {
        const errorText = await res.text();
        console.error('❌ Messages fetch failed:', res.status, errorText);
      }
    } catch (err) {
      console.error('❌ Error fetching messages:', err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (selectedFiles.length + files.length > MAX_FILES) {
      setUploadError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }
    
    const validFiles = [];
    const errors = [];
    
    for (const file of files) {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    }
    
    if (errors.length > 0) {
      setUploadError(errors.join('; '));
    } else {
      setUploadError(null);
    }
    
    setSelectedFiles([...selectedFiles, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setUploadError(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedThread || !assistant?.id) return;

    setSending(true);
    setUploadError(null);
    
    try {
      let attachments = [];
      
      // Upload files if any
      if (selectedFiles.length > 0) {
        try {
          attachments = await uploadMultipleFiles(selectedFiles, (progress) => {
            setUploadProgress(progress);
          });
        } catch (uploadErr) {
          console.error('File upload failed:', uploadErr);
          setUploadError('Failed to upload files. Please try again.');
          setSending(false);
          return;
        }
      }

      const payload = {
        from: 'admin_proxy',
        body: newMessage.trim(),
        executive_id: selectedThread.executive_id,
        executive_name: selectedThread.executive_name,
        attachments: attachments
      };
      
      console.log('📤 Sending message:', payload);

      const res = await fetch(
        `${BACKEND_URL}/api/assistants/${assistant.id}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-key': ADMIN_KEY
          },
          body: JSON.stringify(payload)
        }
      );

      console.log('📤 Send response status:', res.status);
      
      if (res.ok) {
        const responseData = await res.json();
        console.log('✅ Message sent:', responseData);
        success('Sent', `Message sent as ${selectedThread.executive_name}`);
        setNewMessage('');
        setSelectedFiles([]);
        setUploadProgress(0);
        fetchMessages();
        fetchThreads();
      } else {
        const errorData = await res.text();
        console.error('❌ Send failed:', res.status, errorData);
        setUploadError('Failed to send message');
      }
    } catch (err) {
      console.error('❌ Network error:', err);
      setUploadError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Debug Info (temporary) */}
      <div style={{
        padding: '8px 12px',
        background: '#FEF3C7',
        border: '1px solid #FCD34D',
        borderRadius: '6px',
        marginBottom: '16px',
        fontSize: '12px',
        fontFamily: 'monospace'
      }}>
        <strong>Debug:</strong> Assistant ID: {assistant?.id || 'N/A'} | Executive IDs: {assistant?.executive_ids?.join(', ') || 'None'}
        {error && <div style={{ color: '#DC2626', marginTop: '4px' }}>Error: {error}</div>}
      </div>

      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
          View and respond to messages between {assistant?.full_name} and their assigned executive(s). Messages you send will appear to the assistant as if sent by the executive.
        </p>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
          Loading threads...
        </div>
      )}

      {error && !loading && (
        <div style={{
          padding: '20px',
          background: '#FEE2E2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          color: '#991B1B',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && !error && threads.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
          <MessageSquare size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
          <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 12px 0' }}>
            {assistant?.executive_ids?.length > 0 
              ? 'No messages yet. Start the conversation!'
              : 'No executive assignments yet. Assign an executive to this assistant to enable messaging.'}
          </p>
          {(!assistant?.executive_ids || assistant.executive_ids.length === 0) && (
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                marginRight: '8px'
              }}
            >
              Refresh Page
            </button>
          )}
        </div>
      )}

      {!loading && threads.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px', height: '600px' }}>
          {/* Thread List */}
          <div style={{
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '16px',
              background: '#F9FAFB',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h4 style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                Conversations ({threads.length})
              </h4>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {threads.map((thread) => (
                <div
                  key={thread.thread_id}
                  onClick={() => setSelectedThread(thread)}
                  style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid #F3F4F6',
                    cursor: 'pointer',
                    background: selectedThread?.thread_id === thread.thread_id ? '#EFF6FF' : '#FFFFFF',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedThread?.thread_id !== thread.thread_id) {
                      e.currentTarget.style.background = '#F9FAFB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedThread?.thread_id !== thread.thread_id) {
                      e.currentTarget.style.background = '#FFFFFF';
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <h5 style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                      {thread.executive_name}
                    </h5>
                    {thread.message_count > 0 && (
                      <span style={{
                        padding: '2px 6px',
                        background: '#E5E7EB',
                        color: '#6B7280',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {thread.message_count}
                      </span>
                    )}
                  </div>
                  <p style={{
                    color: '#6B7280',
                    fontSize: '13px',
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {thread.last_message || 'No messages yet'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Chat View */}
          {selectedThread && (
            <div style={{
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* Chat Header */}
              <div style={{
                padding: '16px 20px',
                background: '#F9FAFB',
                borderBottom: '1px solid #E5E7EB'
              }}>
                <h4 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '600', margin: '0 0 4px 0' }}>
                  {selectedThread.executive_name}
                </h4>
                <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                  {selectedThread.executive_email}
                </p>
              </div>

              {/* Messages */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px',
                background: '#F8FAFC',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '32px', fontSize: '14px' }}>
                    No messages yet. Start the conversation!
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isAssistant = msg.from === 'assistant';
                    const isAdminProxy = msg.from === 'admin_proxy';
                    
                    return (
                      <div
                        key={msg.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: isAssistant ? 'flex-start' : 'flex-end',
                          marginBottom: '16px'
                        }}
                      >
                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            marginBottom: '8px',
                            maxWidth: '70%'
                          }}>
                            {msg.attachments.map((attachment, idx) => (
                              <MessageAttachment key={idx} attachment={attachment} />
                            ))}
                          </div>
                        )}
                        
                        {/* Text message */}
                        {msg.body && (
                          <div style={{ maxWidth: '70%' }}>
                            <div style={{
                              padding: '12px 16px',
                              borderRadius: '16px',
                              background: isAssistant ? '#FFFFFF' : '#667eea',
                              color: isAssistant ? '#1E293B' : '#FFFFFF',
                              fontSize: '14px',
                              lineHeight: '1.5',
                              border: isAssistant ? '1px solid #E2E8F0' : 'none',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                              wordWrap: 'break-word'
                            }}>
                              {msg.body}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              opacity: 0.7,
                              marginTop: '4px',
                              color: '#64748B',
                              textAlign: isAssistant ? 'left' : 'right',
                              paddingLeft: isAssistant ? '8px' : '0',
                              paddingRight: isAssistant ? '0' : '8px',
                              display: 'flex',
                              gap: '8px',
                              justifyContent: isAssistant ? 'flex-start' : 'flex-end',
                              alignItems: 'center'
                            }}>
                              <span>{formatTimestamp(msg.created_at)}</span>
                              {isAdminProxy && (
                                <span style={{
                                  padding: '2px 6px',
                                  background: '#FEF3C7',
                                  color: '#92400E',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}>
                                  Sent by Admin
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Send Message Form */}
              <div style={{
                padding: '16px 20px',
                background: '#FFFFFF',
                borderTop: '1px solid #E5E7EB'
              }}>
                <div style={{
                  padding: '8px 12px',
                  background: '#EFF6FF',
                  border: '1px solid #BFDBFE',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#1E40AF',
                  marginBottom: '12px'
                }}>
                  💼 Sending as: <strong>{selectedThread.executive_name}</strong> (Assistant will see this as coming from their executive)
                </div>
                
                {/* File Previews */}
                {selectedFiles.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '12px',
                    flexWrap: 'wrap'
                  }}>
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} style={{
                        padding: '8px 14px',
                        background: '#F3F4F6',
                        borderRadius: '18px',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Paperclip size={14} color="#6B7280" />
                        <span style={{ color: '#374151' }}>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            display: 'flex',
                            color: '#6B7280'
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Progress */}
                {sending && selectedFiles.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      height: '4px',
                      background: '#E5E7EB',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        background: '#667eea',
                        width: `${uploadProgress}%`,
                        transition: 'width 0.3s'
                      }} />
                    </div>
                    <p style={{ fontSize: '12px', color: '#6B7280', margin: '6px 0 0 0' }}>
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                )}
                
                {/* Error Message */}
                {uploadError && (
                  <div style={{
                    padding: '10px',
                    background: '#FEE2E2',
                    border: '1px solid #FCA5A5',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#991B1B',
                    marginBottom: '12px'
                  }}>
                    {uploadError}
                  </div>
                )}
                
                <form onSubmit={handleSendMessage} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf,.docx,.mp4"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sending || selectedFiles.length >= MAX_FILES}
                      style={{
                        padding: '12px',
                        background: 'transparent',
                        border: '1px solid #E2E8F0',
                        borderRadius: '10px',
                        cursor: sending || selectedFiles.length >= MAX_FILES ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: sending || selectedFiles.length >= MAX_FILES ? 0.5 : 1
                      }}
                    >
                      <Paperclip size={20} color="#6B7280" />
                    </button>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message…"
                      disabled={sending}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: '1px solid #E2E8F0',
                        fontSize: '14px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending}
                      style={{
                        padding: '12px 20px',
                        borderRadius: '10px',
                        background: (newMessage.trim() || selectedFiles.length > 0) && !sending ? '#667eea' : '#CBD5E1',
                        color: '#FFFFFF',
                        border: 'none',
                        cursor: (newMessage.trim() || selectedFiles.length > 0) && !sending ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {sending ? 'Sending...' : (
                        <>
                          <Send size={16} />
                          Send
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminOpsPage;
